// Custom hooks for manager approval queue (VAC-8)
// Implements data fetching and mutations for approval workflow

import { useState, useEffect, useCallback } from 'react'
import { VacationApprovalService } from '@/lib/services/vacation-approval.service'
import type {
  ApprovalQueueResponse,
  CalendarResponse,
  VacationBalance,
  VacationRequest,
  ApproveRequestPayload,
  RejectRequestPayload
} from '@/types/vacation'

// ============================================================================
// Approval Queue Hook
// ============================================================================

interface UseApprovalQueueResult {
  data: ApprovalQueueResponse | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
  lastUpdated: Date | null
}

/**
 * Hook for fetching pending approval requests
 * Implements caching with 5-minute stale time
 */
export const useApprovalQueue = (page = 1, pageSize = 20): UseApprovalQueueResult => {
  const [data, setData] = useState<ApprovalQueueResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const CACHE_KEY = `approval-queue-${page}-${pageSize}`
  const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  const fetchQueue = useCallback(async (forceRefresh = false) => {
    setIsLoading(true)
    setError(null)

    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY)
        if (cached) {
          const { data: cachedData, timestamp } = JSON.parse(cached)
          const age = Date.now() - timestamp

          if (age < CACHE_DURATION) {
            setData(cachedData)
            setLastUpdated(new Date(timestamp))
            setIsLoading(false)
            return
          }
        }
      }

      // Fetch fresh data
      const response = await VacationApprovalService.getPendingApprovals(page, pageSize)
      setData(response)
      setLastUpdated(new Date())

      // Update cache
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: response,
          timestamp: Date.now()
        })
      )
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to fetch approval queue')
      setError(errorObj)
    } finally {
      setIsLoading(false)
    }
  }, [page, pageSize, CACHE_KEY, CACHE_DURATION])

  const refetch = useCallback(async () => {
    await fetchQueue(true)
  }, [fetchQueue])

  useEffect(() => {
    fetchQueue()
  }, [fetchQueue])

  return {
    data,
    isLoading,
    error,
    refetch,
    lastUpdated
  }
}

// ============================================================================
// Approve Request Hook
// ============================================================================

interface UseApproveRequestResult {
  mutate: (requestId: string, payload: ApproveRequestPayload) => Promise<VacationRequest>
  isLoading: boolean
  error: Error | null
  reset: () => void
}

/**
 * Hook for approving vacation requests
 * Automatically invalidates approval queue cache on success
 */
export const useApproveRequest = (): UseApproveRequestResult => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const mutate = useCallback(async (requestId: string, payload: ApproveRequestPayload) => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await VacationApprovalService.approveRequest(requestId, payload)

      // Invalidate approval queue cache
      const cacheKeys = Object.keys(localStorage).filter(key => key.startsWith('approval-queue-'))
      cacheKeys.forEach(key => localStorage.removeItem(key))

      // Invalidate calendar cache
      const calendarKeys = Object.keys(localStorage).filter(key => key.startsWith('vacation-calendar-'))
      calendarKeys.forEach(key => localStorage.removeItem(key))

      return result
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to approve request')
      setError(errorObj)
      throw errorObj
    } finally {
      setIsLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setError(null)
    setIsLoading(false)
  }, [])

  return {
    mutate,
    isLoading,
    error,
    reset
  }
}

// ============================================================================
// Reject Request Hook
// ============================================================================

interface UseRejectRequestResult {
  mutate: (requestId: string, payload: RejectRequestPayload) => Promise<VacationRequest>
  isLoading: boolean
  error: Error | null
  reset: () => void
}

/**
 * Hook for rejecting vacation requests
 * Automatically invalidates approval queue cache on success
 */
export const useRejectRequest = (): UseRejectRequestResult => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const mutate = useCallback(async (requestId: string, payload: RejectRequestPayload) => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await VacationApprovalService.rejectRequest(requestId, payload)

      // Invalidate approval queue cache
      const cacheKeys = Object.keys(localStorage).filter(key => key.startsWith('approval-queue-'))
      cacheKeys.forEach(key => localStorage.removeItem(key))

      return result
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to reject request')
      setError(errorObj)
      throw errorObj
    } finally {
      setIsLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setError(null)
    setIsLoading(false)
  }, [])

  return {
    mutate,
    isLoading,
    error,
    reset
  }
}

// ============================================================================
// Employee Balance Hook
// ============================================================================

interface UseEmployeeBalanceResult {
  data: VacationBalance | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

/**
 * Hook for fetching employee vacation balance
 * Used in approval context panel
 */
export const useEmployeeBalance = (employeeId: string, enabled = true): UseEmployeeBalanceResult => {
  const [data, setData] = useState<VacationBalance | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchBalance = useCallback(async () => {
    if (!enabled || !employeeId) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await VacationApprovalService.getEmployeeBalance(employeeId)
      setData(response)
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to fetch employee balance')
      setError(errorObj)
    } finally {
      setIsLoading(false)
    }
  }, [employeeId, enabled])

  const refetch = useCallback(async () => {
    await fetchBalance()
  }, [fetchBalance])

  useEffect(() => {
    fetchBalance()
  }, [fetchBalance])

  return {
    data,
    isLoading,
    error,
    refetch
  }
}

// ============================================================================
// Vacation Calendar Hook
// ============================================================================

interface UseVacationCalendarResult {
  data: CalendarResponse | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
  lastUpdated: Date | null
}

/**
 * Hook for fetching team vacation calendar
 * Implements caching with 5-minute stale time per date range
 */
export const useVacationCalendar = (
  startDate: string,
  endDate: string,
  employeeId?: string,
  status: 'APPROVED' | 'PENDING' | 'ALL' = 'APPROVED',
  enabled = true
): UseVacationCalendarResult => {
  const [data, setData] = useState<CalendarResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const CACHE_KEY = `vacation-calendar-${startDate}-${endDate}-${employeeId || 'all'}-${status}`
  const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  const fetchCalendar = useCallback(async (forceRefresh = false) => {
    if (!enabled || !startDate || !endDate) return

    setIsLoading(true)
    setError(null)

    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY)
        if (cached) {
          const { data: cachedData, timestamp } = JSON.parse(cached)
          const age = Date.now() - timestamp

          if (age < CACHE_DURATION) {
            setData(cachedData)
            setLastUpdated(new Date(timestamp))
            setIsLoading(false)
            return
          }
        }
      }

      // Fetch fresh data
      const response = await VacationApprovalService.getTeamCalendar(startDate, endDate, employeeId, status)
      setData(response)
      setLastUpdated(new Date())

      // Update cache
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: response,
          timestamp: Date.now()
        })
      )
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to fetch vacation calendar')
      setError(errorObj)
    } finally {
      setIsLoading(false)
    }
  }, [enabled, startDate, endDate, employeeId, status, CACHE_KEY, CACHE_DURATION])

  const refetch = useCallback(async () => {
    await fetchCalendar(true)
  }, [fetchCalendar])

  useEffect(() => {
    fetchCalendar()
  }, [fetchCalendar])

  return {
    data,
    isLoading,
    error,
    refetch,
    lastUpdated
  }
}
